import { ChangeEvent, useState, useEffect, Fragment, useCallback } from 'react';

import {
  CartMessages,
  CouponDetails,
  Discounts,
  useCheckoutContext,
} from '@zooplus/checkout-state';
import { isBitiba } from '@zooplus/commons-lang';
import { Coupon, AlertTriangle } from '@zooplus/icons';
import { H3, Anchor, P1, Input, PrimaryButton } from '@zooplus/ui-components';

import FeedbackMessage from '../../../common/notifications/feedback-message';
import styles from '../styles/coupons.module.scss';

import { getAppConfig } from '@config/get-app-config';
import usePageTracking from '@hooks/use-adobe-tracking/use-page-tracking';
import { useTranslation } from '@hooks/use-translations';
import {
  CART_VERSION,
  CHECKOUT_STATE_PROJECT_ID,
  FIRST_ORDER_DISCOUNT,
  MESSAGE_CODE,
  MESSAGE_PLACEMENT,
  TARGET_ACTION_ID,
  TARGET_COMPONENT_NAME,
  TRACKING_DATA_EVENT,
} from '@utilities/constants';
import { handleKeyboardKeyDown, isObjectNotNullOrEmpty, isArrayNotEmpty } from '@utilities/utils';

const Coupons = () => {
  const { checkoutState, setCoupon, deleteCoupon, cartVersion } =
    useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { site } = getAppConfig();
  const { t } = useTranslation();

  const { trackEventUsingTrackerListener } = usePageTracking();

  const [activeCoupon, setActiveCoupon] = useState('');
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [showEnterCouponLink, setShowEnterCouponLink] = useState(true);
  const [showInlineMessage, setshowInlineMessage] = useState(false);
  const [showInlineMessageOnce, setshowInlineMessageOnce] = useState(true);
  const [inlineMessageText, setInlineMessageText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const discounts = checkoutState?.cart?.discounts;
  const cartMessages = checkoutState?.cart?.cartMessages;
  const couponResult = checkoutState?.couponResult;
  const isCartApiV2: boolean = cartVersion === CART_VERSION.V2;

  useEffect(() => {
    setIsLoading(false);
    if (
      !isObjectNotNullOrEmpty(couponResult) ||
      couponResult?.couponRemoved ||
      !isArrayNotEmpty(discounts as Discounts[])
    ) {
      setShowEnterCouponLink(true);
    }
    discounts?.forEach((discount) => {
      const couponDetails = (isCartApiV2 ? discount.couponDetails : discount) as CouponDetails;
      couponDetails?.couponActive && setActiveCoupon(couponDetails?.couponCode as string);
      if (couponDetails?.couponCode) {
        setShowEnterCouponLink(false);
      }
    });
  }, [couponResult, cartMessages, discounts]);

  useEffect(() => {
    if (isObjectNotNullOrEmpty(couponResult)) {
      couponResult?.validCoupon &&
        trackEventUsingTrackerListener(
          TRACKING_DATA_EVENT.CLICK,
          TARGET_ACTION_ID.ADD_COUPON_SUCCESS,
          TARGET_COMPONENT_NAME.COUPON,
        );
      setShowCouponInput(false);
    }
  }, [couponResult, trackEventUsingTrackerListener]);

  useEffect(() => {
    if (
      isObjectNotNullOrEmpty(couponResult) &&
      (couponResult?.errorOccurred || couponResult?.couponRemoved)
    ) {
      setActiveCoupon('');
      setShowCouponInput(false);
      setShowEnterCouponLink(true);
    }
  }, [couponResult]);

  useEffect(() => {
    isArrayNotEmpty(cartMessages as CartMessages[]) &&
      cartMessages?.forEach((message) => {
        if (message.placements.includes(MESSAGE_PLACEMENT.INLINE_COUPON)) {
          setshowInlineMessage(true);
          setInlineMessageText(message.localizedMessage);
          setShowCouponInput(true);
          setShowEnterCouponLink(false);
          setshowInlineMessageOnce(message.showOnlyOnce);
        }
      });
  }, [cartMessages]);

  const handleCouponInput = (event: ChangeEvent<HTMLInputElement>) => {
    setActiveCoupon(event.target.value);
    showInlineMessageOnce && setshowInlineMessage(false);
  };

  const callTrackerListener = useCallback(
    (targetActionID: TARGET_ACTION_ID) => {
      trackEventUsingTrackerListener(
        TRACKING_DATA_EVENT.CLICK,
        targetActionID,
        TARGET_COMPONENT_NAME.COUPON,
      );
    },
    [trackEventUsingTrackerListener],
  );

  const handleCouponAddition = () => {
    if (activeCoupon) {
      setIsLoading(true);
      setCoupon?.({ couponCode: activeCoupon });
      callTrackerListener(TARGET_ACTION_ID.REDEEM_COUPON);
    } else {
      setShowEnterCouponLink(true);
      setShowCouponInput(false);
    }
  };

  const handleCouponRemoval = (couponCode: string) => {
    deleteCoupon?.({ couponCode: couponCode });
    callTrackerListener(TARGET_ACTION_ID.REMOVE_COUPON);
  };

  const handleEnterCouponBtn = () => {
    setShowCouponInput(true);
    setShowEnterCouponLink(false);
    callTrackerListener(TARGET_ACTION_ID.ADD_COUPON);
  };

  const fodCappingValueReachedMessage: CartMessages | undefined = cartMessages?.find(
    (message: CartMessages) => message.code == MESSAGE_CODE.FOD_CAPPING_REACHED,
  );

  return (
    <div className={styles.couponWrapper} data-testid="coupons-component">
      <H3>{`${t('loy.coupons.component.title')} / ${t(
        'loy.coupons.component.surcharge.title',
      )}`}</H3>

      <div className={styles.couponList}>
        {discounts?.map((discount, index) => {
          const couponCode = (
            isCartApiV2 ? discount.couponDetails?.couponCode : discount.couponCode
          ) as string;

          return (
            <Fragment key={`discount-${index}`}>
              {couponCode ? (
                <div className={styles.discountWrapper}>
                  <span
                    data-testid="discount-name"
                    className={discount.type == FIRST_ORDER_DISCOUNT ? '' : styles.ellipsis}>
                    <Coupon />
                    {fodCappingValueReachedMessage?.localizedMessage || discount.name}
                  </span>
                  <span data-testid="discount-value" className={styles.subtotal}>
                    {discount.formattedSubtotal}
                  </span>
                  <div className={styles.activeCouponWrapper}>
                    <Anchor
                      tabIndex={0}
                      onClick={() => handleCouponRemoval(couponCode)}
                      onKeyDown={(event) =>
                        handleKeyboardKeyDown(event, handleCouponRemoval, activeCoupon)
                      }
                      data-testid="remove-coupon-btn">
                      {t('loy.coupons.component.remove.coupon')}
                    </Anchor>
                  </div>
                </div>
              ) : (
                discount.subtotal !== 0 && (
                  <div className={styles.discountWrapper}>
                    <span data-testid="discount-name">
                      {fodCappingValueReachedMessage?.localizedMessage || discount.name}
                    </span>
                    <span data-testid="discount-value" className={styles.subtotal}>
                      {discount.formattedSubtotal}
                    </span>
                  </div>
                )
              )}
            </Fragment>
          );
        })}
      </div>

      {showEnterCouponLink && !activeCoupon && (
        <Anchor
          isBold
          tabIndex={0}
          className={styles.enterCoupon}
          onClick={handleEnterCouponBtn}
          onKeyDown={(event) => handleKeyboardKeyDown(event, handleEnterCouponBtn)}
          data-testid="enter-coupon-btn">
          {t('loy.coupons.component.enter.coupon')}
        </Anchor>
      )}

      {showCouponInput && (
        <>
          <div className={styles.couponInputBox}>
            <Input
              hasError={showInlineMessage}
              onChange={handleCouponInput}
              onKeyDown={(event) => handleKeyboardKeyDown(event, handleCouponAddition)}
              inputProps={{
                'data-testid': 'coupon-input',
                maxLength: 50,
                placeholder: t('cart.coupon.placeholder'),
              }}
            />
            <PrimaryButton
              isLoading={isLoading}
              onClick={handleCouponAddition}
              size={'small'}
              loadingIconType={isBitiba(site.domain) ? 'spinner' : 'paw'}
              data-testid="redeem-coupon-button">
              {t('cart.coupon.apply')}
            </PrimaryButton>
          </div>
          <div className={styles.couponErrorMessage}>
            {showInlineMessage && inlineMessageText && (
              <div data-testid="inline-coupon-message">
                <AlertTriangle />
                <P1 className={styles.couponInfoText}>{inlineMessageText}</P1>
              </div>
            )}
          </div>
        </>
      )}

      <FeedbackMessage placement={MESSAGE_PLACEMENT.COUPON} />
    </div>
  );
};

export default Coupons;
